/** PROTOTYPE state — in memory only. */
import { useCallback, useMemo, useState } from 'react'
import { EVENT, findRef, type ProtoRef } from './seed'

export type Room = 'attendance' | 'plan'

export type Draft = {
	action: string
	details: string
	assigneeIds: string[]
	references: ProtoRef[]
}

export function usePrototypeState() {
	const [room, setRoom] = useState<Room>('attendance')
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState({ x: 72, y: 96 })
	const [hoveredId, setHoveredId] = useState<string | null>(null)
	const [draft, setDraft] = useState<Draft>(emptyDraft())
	const [saved, setSaved] = useState<Draft[]>([])
	const [log, setLog] = useState<string[]>(['Ready. Hover a thing and press T, or hit the control left of the avatar.'])

	const ambient = useMemo(() => [EVENT], [])

	const note = useCallback((line: string) => {
		setLog((prev) => [line, ...prev].slice(0, 8))
	}, [])

	const openComposer = useCallback(
		(seed?: ProtoRef) => {
			setDraft((prev) => {
				if (open) return prev
				const refs = mergeRefs(ambient, seed ? [seed] : [])
				return { ...emptyDraft(), references: refs }
			})
			setOpen(true)
			note(
				seed
					? `Opened Composer with ${seed.name} + ambient ${EVENT.name}`
					: `Opened Composer with ambient ${EVENT.name}`,
			)
		},
		[ambient, note, open],
	)

	const addRef = useCallback(
		(ref: ProtoRef) => {
			setDraft((prev) => {
				if (prev.references.some((r) => r.id === ref.id)) {
					note(`Already referenced: ${ref.name}`)
					return prev
				}
				note(`Added Reference: ${ref.name}`)
				return { ...prev, references: [...prev.references, ref] }
			})
		},
		[note],
	)

	const removeRef = useCallback((id: string) => {
		setDraft((prev) => ({
			...prev,
			references: prev.references.filter((r) => r.id !== id),
		}))
		note(`Removed Reference ${id}`)
	}, [note])

	const handleT = useCallback(() => {
		const hovered = hoveredId ? findRef(hoveredId) : undefined
		if (!open) {
			openComposer(hovered)
			return
		}
		if (hovered) addRef(hovered)
		else note('T while open, nothing hovered — no change')
	}, [addRef, hoveredId, note, open, openComposer])

	const save = useCallback(() => {
		if (!draft.action.trim()) {
			note('Action required to save (prototype)')
			return
		}
		setSaved((prev) => [draft, ...prev])
		setOpen(false)
		setDraft(emptyDraft())
		note(`Saved Task: ${draft.action}`)
	}, [draft, note])

	const close = useCallback(() => {
		setOpen(false)
		note('Closed Composer (draft discarded — prototype has no persist)')
	}, [note])

	const newTask = useCallback(() => {
		setDraft({ ...emptyDraft(), references: [...ambient] })
		setOpen(true)
		note('Explicit New Task — ambient Event attached')
	}, [ambient, note])

	return {
		room,
		setRoom,
		open,
		pos,
		setPos,
		hoveredId,
		setHoveredId,
		draft,
		setDraft,
		saved,
		log,
		ambient,
		openComposer,
		addRef,
		removeRef,
		handleT,
		save,
		close,
		newTask,
	}
}

export type PrototypeState = ReturnType<typeof usePrototypeState>

function emptyDraft(): Draft {
	return { action: '', details: '', assigneeIds: [], references: [] }
}

function mergeRefs(...groups: ProtoRef[][]): ProtoRef[] {
	const map = new Map<string, ProtoRef>()
	for (const group of groups) {
		for (const ref of group) map.set(ref.id, ref)
	}
	return [...map.values()]
}
